import { useRef, useCallback } from 'react'

/**
 * Hook for swipe-down-to-dismiss on mobile bottom sheets.
 * Returns touch handlers + a ref for the sheet element.
 *
 * @param {Function} onDismiss - called when swipe threshold is reached
 * @param {number} threshold - pixels to swipe before dismissing (default 100)
 */
export function useSwipeDismiss(onDismiss, threshold = 100) {
  const sheetRef  = useRef(null)
  const startY    = useRef(0)
  const currentY  = useRef(0)
  const swiping   = useRef(false)

  const onTouchStart = useCallback((e) => {
    // Only start swipe from the top area (handle + header, first 60px)
    const sheet = sheetRef.current
    if (!sheet) return
    const rect = sheet.getBoundingClientRect()
    const touchY = e.touches[0].clientY - rect.top
    if (touchY > 60) return // don't interfere with content scroll

    startY.current  = e.touches[0].clientY
    currentY.current = 0
    swiping.current = false
  }, [])

  const onTouchMove = useCallback((e) => {
    if (startY.current === 0) return
    const dy = e.touches[0].clientY - startY.current
    if (!swiping.current && dy > 10) swiping.current = true
    if (!swiping.current || dy < 0) return

    currentY.current = dy
    const sheet = sheetRef.current
    if (sheet) {
      sheet.style.transform = `translateY(${dy}px)`
      sheet.style.transition = 'none'
    }
  }, [])

  const onTouchEnd = useCallback(() => {
    if (startY.current === 0) return
    const sheet = sheetRef.current
    startY.current = 0

    if (!swiping.current || !sheet) {
      if (sheet) {
        sheet.style.transform = ''
        sheet.style.transition = ''
      }
      return
    }

    if (currentY.current > threshold) {
      // Dismiss with animation
      sheet.style.transition = 'transform 0.2s ease'
      sheet.style.transform = 'translateY(100%)'
      setTimeout(() => onDismiss(), 200)
    } else {
      // Snap back
      sheet.style.transition = 'transform 0.2s ease'
      sheet.style.transform = ''
      setTimeout(() => { sheet.style.transition = '' }, 200)
    }

    swiping.current = false
    currentY.current = 0
  }, [onDismiss, threshold])

  return { sheetRef, onTouchStart, onTouchMove, onTouchEnd }
}
